import type { DocumentRow } from '../types';
import type { SearchParams } from 'atoms/search-atom';
import type { SubsurfaceMapRow } from '../subsurface-maps/types';
import type { RowData } from './types';

export type ExpandAllBroadcast = { open: boolean };

// "Expand all" opens each card, and every open fires its own detail fetch. The
// limit matches the max page size so a full page expands; it only caps a page
// that somehow exceeds it. Collapse still applies to every card.
export const EXPAND_ALL_LIMIT = 100;

// What a card shows where a value or list is missing.
export const NOT_AVAILABLE = 'Not Available';

/**
 * The expand-all broadcast for the card at an index: collapse reaches every
 * card, expand only the first EXPAND_ALL_LIMIT.
 * @param {ExpandAllBroadcast | undefined} expandAll The broadcast.
 * @param {number} index The card's position on the page.
 * @return {ExpandAllBroadcast | undefined} The card's broadcast.
 */
export const cappedExpandAll = (
  expandAll: ExpandAllBroadcast | undefined,
  index: number
): ExpandAllBroadcast | undefined => {
  if (!expandAll) return undefined;
  return !expandAll.open || index < EXPAND_ALL_LIMIT ? expandAll : undefined;
};

export const TYPE_LABELS: Record<string, string> = {
  'Document::Courthouse': 'Courthouse',
  'Document::NonCourthouse': 'Non-Courthouse',
  'Document::Regulatory': 'Regulatory',
  'Document::Afe': 'AFE',
  'Document::DivisionOrder': 'Division Order',
  'Document::RoyaltyCheck': 'Royalty Check',
};

// Document types whose expand section is type-specific (AFE cost/ownership, DO/RC
// line items), lazy-loaded on expand — not the interpretation-driven expand.
const TYPED_EXPAND_TYPES = new Set([
  'Document::Afe',
  'Document::DivisionOrder',
  'Document::RoyaltyCheck',
]);

/**
 * Whether a document's expand section is type-specific (AFE/DO/RC).
 * @param {string | undefined | null} type The document's STI type.
 * @return {boolean} True for a typed expand.
 */
export const isTypedExpandType = (type: string | undefined | null) =>
  type ? TYPED_EXPAND_TYPES.has(type) : false;

/**
 * The card title: "Type | Subtype | ST-County", falling back to the document's
 * title. Mock format: hyphen with no spaces and the trailing "County"/"Parish"
 * stripped. Shows book_type (documentSubtype) so the title matches the
 * "Document Subtype" color rule; falls back to the free-text document_type.
 * @param {DocumentRow} document The document.
 * @return {string} The title text.
 */
export const cardTitleText = (document: DocumentRow): string => {
  const typeLabel = document.type
    ? (TYPE_LABELS[document.type] ?? document.type)
    : '';
  const name = document.documentSubtype || document.documentType || '';
  const countyShort = document.countyName?.replace(
    /\s+(County|Parish|Borough)$/i,
    ''
  );
  const location = [document.stateAbbreviation, countyShort]
    .filter(Boolean)
    .join('-');
  return (
    [typeLabel, name, location].filter(Boolean).join(' | ') ||
    document.title ||
    'Document'
  );
};

// The Wells dataset alone carries the View dropdown: a workspace is a set of wells,
// so the workspaces view belongs beside the wells and nowhere else.
export const WELLS_DATASET_ID = 'wells';

// The element a page provides for the document preview rail to portal into.
export const PREVIEW_HOST_ID = 'mq-preview-host';

/**
 * A row's field as display text: its value when a non-empty string.
 * @param {unknown} value The field's value.
 * @return {string | undefined} The text, or nothing when blank.
 */
const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

/**
 * Header label for the preview panel: the row's title, else its document type
 * (the viewer itself defaults to "Document #id" when unnamed).
 * @param {RowData | undefined} row The previewed row.
 * @return {string | undefined} The label.
 */
export const previewName = (row: RowData | undefined): string | undefined =>
  text(row?.title) ?? text(row?.documentType);

/**
 * Second header line for the preview panel, e.g. "Courthouse · Martin County, TX".
 * @param {RowData | undefined} row The previewed row.
 * @return {string | undefined} The line.
 */
export const previewSubtitle = (
  row: RowData | undefined
): string | undefined => {
  if (!row) return undefined;
  const type = text(row.type);
  const typeLabel = type ? (TYPE_LABELS[type] ?? type) : '';
  const place = [row.countyName, row.stateAbbreviation]
    .filter(Boolean)
    .join(', ');
  return [typeLabel, place].filter(Boolean).join(' · ') || undefined;
};

/**
 * The page and page size a search last ran with, else the dataset's defaults.
 * @param {SearchParams | undefined} payload The last searched payload.
 * @param {SearchParams} defaults The dataset's default payload.
 * @return {{ page: number, pageSize: number }} The paging.
 */
export const currentPaging = (
  payload: SearchParams | undefined,
  defaults: SearchParams
) => ({
  page: payload?.page ?? defaults.page ?? 1,
  pageSize: payload?.pageSize ?? defaults.pageSize ?? 100,
});

/**
 * Whether a search row is a document (the runsheet's rows): it carries an id.
 * @param {RowData} row The row.
 * @return {boolean} True for a document row.
 */
export const isDocumentRow = (row: RowData): row is RowData & DocumentRow =>
  typeof row.id === 'number' || typeof row.id === 'string';

/**
 * Whether a search row is a subsurface map (the raster locator's rows).
 * @param {RowData} row The row.
 * @return {boolean} True for a subsurface map row.
 */
export const isSubsurfaceMapRow = (
  row: RowData
): row is RowData & SubsurfaceMapRow =>
  typeof row.id === 'number' && typeof row.name === 'string';

/** A row's bounds as the backend sends them: corner to corner, or already an extent. */
interface BoundingBox {
  swLng: number;
  swLat: number;
  neLng: number;
  neLat: number;
}

/**
 * The [west, south, east, north] a row's "Show on Map" fits to. Subsurface maps
 * carry the array; areas carry a `boundingBox` of corners (null until indexed).
 *
 * @param {RowData} row - The table row.
 * @param {string | undefined} key - The dataset's `extentKey`.
 * @return {[number, number, number, number] | null} The extent, or null without one.
 */
export const rowExtent = (
  row: RowData,
  key: string | undefined
): [number, number, number, number] | null => {
  const value = key ? row[key] : null;
  if (Array.isArray(value) && value.length === 4) {
    return value as [number, number, number, number];
  }
  if (value && typeof value === 'object' && 'swLng' in value) {
    const box = value as BoundingBox;
    return [box.swLng, box.swLat, box.neLng, box.neLat];
  }
  return null;
};
